import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Firebase config kontrolü
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "",
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "",
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "",
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "",
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "",
    appId: process.env.REACT_APP_FIREBASE_APP_ID || ""
};

// Firebase config var mı kontrol et
const isFirebaseConfigured = Boolean(firebaseConfig.apiKey && firebaseConfig.projectId);

// User tipi
interface User {
    uid: string;
    email: string | null;
}

// Auth context tipi
interface AuthContextType {
    user: User | null;
    loading: boolean;
    error: string | null;
    login: (email: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
    isDemo: boolean; // Demo modunda mı?
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth Provider
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        // Firebase config yoksa hemen loading'i kapat
        if (!isFirebaseConfigured) {
            console.warn('Firebase yapılandırılmamış. Demo modunda çalışıyor.');
            setLoading(false);
            return;
        }

        // Firebase config varsa asıl Firebase auth'u yükle
        const initFirebase = async () => {
            try {
                const { initializeApp } = await import('firebase/app');
                const { getAuth, onAuthStateChanged } = await import('firebase/auth');

                const app = initializeApp(firebaseConfig);
                const auth = getAuth(app);

                onAuthStateChanged(auth, (firebaseUser) => {
                    if (firebaseUser) {
                        setUser({
                            uid: firebaseUser.uid,
                            email: firebaseUser.email
                        });
                    } else {
                        setUser(null);
                    }
                    setLoading(false);
                });
            } catch (err) {
                console.error('Firebase başlatma hatası:', err);
                setLoading(false);
            }
        };

        initFirebase();
    }, []);

    const login = async (email: string, password: string) => {
        try {
            setError(null);
            setLoading(true);

            if (!isFirebaseConfigured) {
                // Demo modu - basit doğrulama
                // Test kullanıcı: admin@windlar.com / admin123
                if (email === 'admin@windlar.com' && password === 'admin123') {
                    setUser({
                        uid: 'demo-user-001',
                        email: email
                    });
                } else {
                    throw new Error('Geçersiz kullanıcı adı veya şifre.');
                }
            } else {
                // Gerçek Firebase auth
                const { getAuth, signInWithEmailAndPassword } = await import('firebase/auth');
                const auth = getAuth();
                const result = await signInWithEmailAndPassword(auth, email, password);
                setUser({
                    uid: result.user.uid,
                    email: result.user.email
                });
            }
        } catch (err: any) {
            let errorMessage = 'Giriş başarısız.';

            if (err.code === 'auth/user-not-found') {
                errorMessage = 'Kullanıcı bulunamadı.';
            } else if (err.code === 'auth/wrong-password') {
                errorMessage = 'Hatalı şifre.';
            } else if (err.code === 'auth/invalid-email') {
                errorMessage = 'Geçersiz e-posta adresi.';
            } else if (err.code === 'auth/too-many-requests') {
                errorMessage = 'Çok fazla deneme. Lütfen daha sonra tekrar deneyin.';
            } else if (err.message) {
                errorMessage = err.message;
            }

            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        try {
            if (isFirebaseConfigured) {
                const { getAuth, signOut } = await import('firebase/auth');
                const auth = getAuth();
                await signOut(auth);
            }
            setUser(null);
        } catch (err) {
            console.error('Çıkış hatası:', err);
        }
    };

    return (
        <AuthContext.Provider value={{
            user,
            loading,
            error,
            login,
            logout,
            isDemo: !isFirebaseConfigured
        }}>
            {children}
        </AuthContext.Provider>
    );
};

// useAuth hook
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth hook AuthProvider içinde kullanılmalıdır');
    }
    return context;
};
